// src/pages/data-sources/collection-page/hooks/use-chat-settings.ts
import { useState, useCallback } from 'react';
import { useNotification } from '@/components/notifications';
import { updateCollection } from '@/services/data-sources/update-collection';
import type { Collection } from '@/types/source-types';
import type { CollectionChatPromptSource } from '@/services/data-sources/get-collection-chat-system-prompt';

type ChatFilterRule = {
  keywords: string[];
  key: string;
  value: string | string[];
};

// Helper to filter config to only include valid backend QdrantConfig fields
const sanitizeQdrantConfig = (
  config: Record<string, unknown> | undefined,
): Record<string, unknown> => {
  if (!config) return {};

  const validFields = [
    'collection_name',
    'shard_count',
    'embedding_dimension',
    'distance_metric',
    'cluster_id',
    'provider',
    'embedding_model',
    'chat_system_prompt',
    'chat_filters',
    'chat_prompts',
  ];

  const sanitized: Record<string, unknown> = {};
  for (const key of validFields) {
    if (key in config) {
      sanitized[key] = config[key];
    }
  }

  // Ensure optional nullable fields are explicitly included (even if missing from source)
  if (!('chat_system_prompt' in sanitized)) {
    sanitized.chat_system_prompt = null;
  }
  if (!('chat_filters' in sanitized)) {
    sanitized.chat_filters = null;
  }
  if (!('chat_prompts' in sanitized)) {
    sanitized.chat_prompts = [];
  }

  return sanitized;
};

interface UseChatSettingsOptions {
  collection: Collection | null;
  collectionId: string | undefined;
  patchCollectionCache: (
    updater: (prev: Collection | null) => Collection | null,
  ) => void;
}

interface UseChatSettingsReturn {
  // Chat prompt modal
  isChatPromptModalOpen: boolean;
  setIsChatPromptModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleChatPromptUpdated: (
    source: CollectionChatPromptSource,
    promptId: string | null,
  ) => void;

  // Chat filters modal
  isChatFiltersModalOpen: boolean;
  setIsChatFiltersModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  chatFiltersText: string;
  setChatFiltersText: React.Dispatch<React.SetStateAction<string>>;
  chatFiltersError: string | null;
  setChatFiltersError: React.Dispatch<React.SetStateAction<string | null>>;
  isChatFiltersSaving: boolean;
  handleOpenChatFilters: () => void;
  handleSaveChatFilters: () => Promise<void>;

  // Saved prompts
  handleSavePrompt: (prompt: string, chatMode: string) => Promise<void>;
  handleDeletePrompt: (index: number) => Promise<void>;
  handleReorderPrompts: (
    prompts: Array<{ text: string; chat_mode: string }>,
  ) => Promise<void>;
}

export function useChatSettings({
  collection,
  collectionId,
  patchCollectionCache,
}: UseChatSettingsOptions): UseChatSettingsReturn {
  const notification = useNotification();

  // Chat prompt modal state
  const [isChatPromptModalOpen, setIsChatPromptModalOpen] = useState(false);

  // Chat filters modal state
  const [isChatFiltersModalOpen, setIsChatFiltersModalOpen] = useState(false);
  const [chatFiltersText, setChatFiltersText] = useState('');
  const [chatFiltersError, setChatFiltersError] = useState<string | null>(null);
  const [isChatFiltersSaving, setIsChatFiltersSaving] = useState(false);

  const handleChatPromptUpdated = useCallback(
    (source: CollectionChatPromptSource, promptId: string | null) => {
      patchCollectionCache((prev) => {
        if (!prev) return prev;
        const nextConfig = { ...(prev.config || {}) };
        if (source === 'custom' && promptId) {
          nextConfig.chat_system_prompt = promptId;
        } else {
          delete nextConfig.chat_system_prompt;
        }
        return { ...prev, config: nextConfig };
      });
    },
    [patchCollectionCache],
  );

  const handleOpenChatFilters = useCallback(() => {
    const chatFilters = (
      collection?.config as { chat_filters?: ChatFilterRule[] } | undefined
    )?.chat_filters;
    setChatFiltersText(
      chatFilters && Array.isArray(chatFilters)
        ? JSON.stringify(chatFilters, null, 2)
        : '',
    );
    setChatFiltersError(null);
    setIsChatFiltersModalOpen(true);
  }, [collection]);

  const handleSaveChatFilters = useCallback(async () => {
    if (!collection || !collectionId) return;
    const trimmed = chatFiltersText.trim();
    let parsed: ChatFilterRule[] | undefined = undefined;
    if (trimmed) {
      try {
        parsed = JSON.parse(trimmed);
        if (!Array.isArray(parsed)) {
          throw new Error('chat_filters must be an array');
        }
      } catch (err) {
        setChatFiltersError(
          err instanceof Error ? err.message : 'Invalid chat_filters JSON.',
        );
        return;
      }
    }
    setChatFiltersError(null);
    setIsChatFiltersSaving(true);
    const nextConfig: Record<string, unknown> = {
      ...sanitizeQdrantConfig(
        collection.config as unknown as Record<string, unknown>,
      ),
    };
    if (parsed) {
      nextConfig.chat_filters = parsed;
    } else {
      delete nextConfig.chat_filters;
    }
    const response = await updateCollection({
      id: collectionId,
      config: nextConfig,
    });
    setIsChatFiltersSaving(false);
    if ('detail' in response && response.detail) {
      notification.error(response.detail || 'Failed to update chat filters.');
      return;
    }
    patchCollectionCache((prev) =>
      prev
        ? { ...prev, config: nextConfig as unknown as Collection['config'] }
        : prev,
    );
    notification.success('Chat filters updated.');
    setIsChatFiltersModalOpen(false);
  }, [
    chatFiltersText,
    collection,
    collectionId,
    notification,
    patchCollectionCache,
  ]);

  const handleSavePrompt = useCallback(
    async (prompt: string, chatMode: string) => {
      if (!collection || !collectionId) return;

      const currentPrompts = collection.config?.chat_prompts || [];

      if (currentPrompts.length >= 10) {
        notification.error('Maximum 10 prompts allowed');
        return;
      }

      if (currentPrompts.some((p) => p.text === prompt)) {
        notification.warning('This prompt is already saved');
        return;
      }

      const newPrompt = { text: prompt, chat_mode: chatMode };
      const updatedPrompts = [...currentPrompts, newPrompt];

      try {
        const response = await updateCollection({
          id: collectionId,
          config: {
            ...sanitizeQdrantConfig(
              collection.config as unknown as Record<string, unknown>,
            ),
            chat_prompts: updatedPrompts,
          },
        });

        if ('detail' in response && response.detail) {
          notification.error(response.detail || 'Failed to save prompt');
          return;
        }

        patchCollectionCache((prev) =>
          prev
            ? {
                ...prev,
                config: {
                  ...prev.config,
                  chat_prompts: updatedPrompts,
                } as Collection['config'],
              }
            : prev,
        );

        notification.success('Prompt saved successfully');
      } catch {
        notification.error('Failed to save prompt');
      }
    },
    [collection, collectionId, notification, patchCollectionCache],
  );

  const handleDeletePrompt = useCallback(
    async (index: number) => {
      if (!collection || !collectionId) return;

      const currentPrompts = collection.config?.chat_prompts || [];
      const updatedPrompts = currentPrompts.filter((_, i) => i !== index);

      try {
        const response = await updateCollection({
          id: collectionId,
          config: {
            ...sanitizeQdrantConfig(
              collection.config as unknown as Record<string, unknown>,
            ),
            chat_prompts: updatedPrompts,
          },
        });

        if ('detail' in response && response.detail) {
          notification.error(response.detail || 'Failed to delete prompt');
          return;
        }

        patchCollectionCache((prev) =>
          prev
            ? {
                ...prev,
                config: {
                  ...prev.config,
                  chat_prompts: updatedPrompts,
                } as Collection['config'],
              }
            : prev,
        );

        notification.success('Prompt deleted');
      } catch {
        notification.error('Failed to delete prompt');
      }
    },
    [collection, collectionId, notification, patchCollectionCache],
  );

  const handleReorderPrompts = useCallback(
    async (prompts: Array<{ text: string; chat_mode: string }>) => {
      if (!collection || !collectionId) return;

      try {
        const response = await updateCollection({
          id: collectionId,
          config: {
            ...sanitizeQdrantConfig(
              collection.config as unknown as Record<string, unknown>,
            ),
            chat_prompts: prompts,
          },
        });

        if ('detail' in response && response.detail) {
          notification.error(response.detail || 'Failed to reorder prompts');
          return;
        }

        patchCollectionCache((prev) =>
          prev
            ? {
                ...prev,
                config: {
                  ...prev.config,
                  chat_prompts: prompts,
                } as Collection['config'],
              }
            : prev,
        );

        notification.success('Changes saved successfully');
      } catch {
        notification.error('Failed to reorder prompts');
      }
    },
    [collection, collectionId, notification, patchCollectionCache],
  );

  return {
    isChatPromptModalOpen,
    setIsChatPromptModalOpen,
    handleChatPromptUpdated,
    isChatFiltersModalOpen,
    setIsChatFiltersModalOpen,
    chatFiltersText,
    setChatFiltersText,
    chatFiltersError,
    setChatFiltersError,
    isChatFiltersSaving,
    handleOpenChatFilters,
    handleSaveChatFilters,
    handleSavePrompt,
    handleDeletePrompt,
    handleReorderPrompts,
  };
}
